import { supabase } from '../lib/supabase'
import {
  fetchJobsByIds,
  resolveAssignedJobsScope,
} from '../lib/jobVisibility'
import { parseEntityNotes, type TimestampNote } from '../lib/entityNotes'

export type CrewPropertyListItem = {
  id: string
  name: string | null
  address: string | null
  address_line_2: string | null
  city: string | null
  state: string | null
  zip_code: string | null
  access_type: string | null
  nextJobDate: string | null
  jobCount: number
}

export type CrewPropertyJob = {
  id: string
  job_number: string | null
  title: string | null
  status: string | null
  scheduled_date: string | null
  scheduled_start_time: string | null
  scheduled_end_time: string | null
  service_type: string | null
}

export type CrewPropertyAttachment = {
  id: string
  job_id: string
  file_url: string
  file_name: string | null
  mime_type: string | null
  attachment_type: string | null
  created_at: string | null
}

export type CrewPropertyDetail = {
  id: string
  name: string | null
  address: string | null
  address_line_2: string | null
  city: string | null
  state: string | null
  zip_code: string | null
  access_type: string | null
  access_info: string | null
  additional_access: Array<{
    location?: string
    access_type?: string
    access_info?: string
  }>
  /** Site notes used as hazards / special instructions for field crew. */
  siteNotes: TimestampNote[]
  /**
   * Reserved for a future opt-in customer contact block.
   * Intentionally not populated for crew today.
   */
  customerContact?: {
    company_name?: string | null
    phone?: string | null
    email?: string | null
  } | null
  todayJobs: CrewPropertyJob[]
  upcomingJobs: CrewPropertyJob[]
  attachments: CrewPropertyAttachment[]
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function addDaysIso(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function formatAddress(p: {
  address?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
}): string {
  return [
    p.address,
    [p.city, p.state].filter(Boolean).join(', '),
    p.zip_code,
  ]
    .filter(Boolean)
    .join(', ')
}

const JOB_SELECT =
  'id, job_number, title, status, scheduled_date, scheduled_start_time, scheduled_end_time, service_type, property_id'

async function fetchAssignedJobsInRange(
  employeeId: string,
  from: string,
  to: string
): Promise<Record<string, unknown>[]> {
  const scope = await resolveAssignedJobsScope(employeeId, {
    scheduledDateFrom: from,
    scheduledDateTo: to,
  })

  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_SELECT)
    .or(scope.orFilter)
    .gte('scheduled_date', from)
    .lte('scheduled_date', to)
    .neq('status', 'cancelled')
    .order('scheduled_date', { ascending: true })

  if (error) throw error

  let rows = (data || []) as Record<string, unknown>[]
  if (scope.extraJobIds.length > 0) {
    const extras = await fetchJobsByIds(scope.extraJobIds, JOB_SELECT)
    const byId = new Map<string, Record<string, unknown>>()
    for (const r of rows) byId.set(String(r.id), r)
    for (const r of extras) {
      const date = String(r.scheduled_date || '')
      if (date < from || date > to) continue
      if (r.status === 'cancelled') continue
      byId.set(String(r.id), r)
    }
    rows = Array.from(byId.values())
  }

  return rows
}

/** Properties tied to the employee's assigned / upcoming jobs (not full catalog). */
export async function fetchCrewPropertiesForEmployee(
  employeeId: string,
  upcomingDays = 45
): Promise<CrewPropertyListItem[]> {
  const from = todayIso()
  const to = addDaysIso(upcomingDays)
  const jobs = await fetchAssignedJobsInRange(employeeId, from, to)

  const byProperty = new Map<
    string,
    { jobCount: number; nextJobDate: string | null }
  >()
  for (const job of jobs) {
    const pid = job.property_id as string | null
    if (!pid) continue
    const date = (job.scheduled_date as string) || null
    const prev = byProperty.get(pid)
    if (!prev) {
      byProperty.set(pid, { jobCount: 1, nextJobDate: date })
    } else {
      prev.jobCount += 1
      if (date && (!prev.nextJobDate || date < prev.nextJobDate)) {
        prev.nextJobDate = date
      }
    }
  }

  const propertyIds = Array.from(byProperty.keys())
  if (propertyIds.length === 0) return []

  const { data: properties, error } = await supabase
    .from('properties')
    .select(
      'id, name, address, address_line_2, city, state, zip_code, access_type'
    )
    .in('id', propertyIds)

  if (error) throw error

  const items: CrewPropertyListItem[] = (properties || []).map((p: any) => {
    const meta = byProperty.get(p.id)!
    return {
      id: p.id,
      name: p.name ?? null,
      address: p.address ?? null,
      address_line_2: p.address_line_2 ?? null,
      city: p.city ?? null,
      state: p.state ?? null,
      zip_code: p.zip_code ?? null,
      access_type: p.access_type ?? null,
      nextJobDate: meta.nextJobDate,
      jobCount: meta.jobCount,
    }
  })

  items.sort((a, b) => {
    const ad = a.nextJobDate || '9999'
    const bd = b.nextJobDate || '9999'
    if (ad !== bd) return ad.localeCompare(bd)
    return (a.name || a.address || '').localeCompare(b.name || b.address || '')
  })

  return items
}

/** True when the property appears on an assigned job in the lookback/upcoming window. */
export async function canAccessCrewProperty(
  employeeId: string,
  propertyId: string,
  lookbackDays = 14,
  upcomingDays = 90
): Promise<boolean> {
  const from = addDaysIso(-lookbackDays)
  const to = addDaysIso(upcomingDays)
  const jobs = await fetchAssignedJobsInRange(employeeId, from, to)
  return jobs.some((j) => j.property_id === propertyId)
}

export async function fetchCrewPropertyDetail(
  employeeId: string,
  propertyId: string
): Promise<CrewPropertyDetail | null> {
  const allowed = await canAccessCrewProperty(employeeId, propertyId)
  if (!allowed) return null

  const { data: property, error } = await supabase
    .from('properties')
    .select(
      'id, name, address, address_line_2, city, state, zip_code, access_type, access_info, additional_access, notes'
    )
    .eq('id', propertyId)
    .maybeSingle()

  if (error) throw error
  if (!property) return null

  const from = todayIso()
  const to = addDaysIso(90)
  const lookbackFrom = addDaysIso(-7)

  const scope = await resolveAssignedJobsScope(employeeId, {
    scheduledDateFrom: lookbackFrom,
    scheduledDateTo: to,
  })

  const { data: jobRows, error: jobErr } = await supabase
    .from('jobs')
    .select(JOB_SELECT)
    .eq('property_id', propertyId)
    .or(scope.orFilter)
    .gte('scheduled_date', lookbackFrom)
    .lte('scheduled_date', to)
    .neq('status', 'cancelled')
    .order('scheduled_date', { ascending: true })

  if (jobErr) throw jobErr

  let jobs = (jobRows || []) as CrewPropertyJob[]
  if (scope.extraJobIds.length > 0) {
    const extras = (await fetchJobsByIds(scope.extraJobIds, JOB_SELECT)) as Array<
      CrewPropertyJob & { property_id?: string }
    >
    const byId = new Map(jobs.map((j) => [j.id, j]))
    for (const j of extras) {
      if (j.property_id !== propertyId) continue
      if (j.status === 'cancelled') continue
      const d = j.scheduled_date || ''
      if (d < lookbackFrom || d > to) continue
      byId.set(j.id, j)
    }
    jobs = Array.from(byId.values()).sort((a, b) =>
      String(a.scheduled_date).localeCompare(String(b.scheduled_date))
    )
  }

  const today = todayIso()
  const todayJobs = jobs.filter((j) => j.scheduled_date === today)
  const upcomingJobs = jobs.filter(
    (j) => j.scheduled_date && j.scheduled_date > today
  )

  const jobIds = jobs.map((j) => j.id)
  let attachments: CrewPropertyAttachment[] = []
  if (jobIds.length > 0) {
    const { data: att, error: attErr } = await supabase
      .from('job_attachments')
      .select('id, job_id, file_url, file_name, mime_type, attachment_type, created_at')
      .in('job_id', jobIds.slice(0, 80))
      .order('created_at', { ascending: false })
      .limit(40)
    if (attErr) throw attErr
    attachments = (att || []) as CrewPropertyAttachment[]
  }

  const additional =
    property.additional_access && Array.isArray(property.additional_access)
      ? property.additional_access
      : []

  return {
    id: property.id,
    name: property.name ?? null,
    address: property.address ?? null,
    address_line_2: property.address_line_2 ?? null,
    city: property.city ?? null,
    state: property.state ?? null,
    zip_code: property.zip_code ?? null,
    access_type: property.access_type ?? null,
    access_info: property.access_info ?? null,
    additional_access: additional,
    siteNotes: parseEntityNotes(property.notes),
    // Intentionally null — structure kept for a later opt-in customer block.
    customerContact: null,
    todayJobs,
    upcomingJobs,
    attachments,
  }
}

export function crewPropertyAddressLine(p: {
  address?: string | null
  address_line_2?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
}): string {
  const line1 = [p.address, p.address_line_2].filter(Boolean).join(', ')
  const line2 = [p.city, p.state].filter(Boolean).join(', ')
  return [line1, line2, p.zip_code].filter(Boolean).join(', ')
}

export { formatAddress as formatCrewPropertyAddress }
